import React, {
  MouseEvent,
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
  TransitionEventHandler,
} from 'react';
import {
  Grid,
  Button,
  ButtonProps,
  Menu,
  MenuItem,
  Paper,
  SxProps,
  Theme,
  Tooltip
} from '@mui/material';

import {
  DiscourseAnnotation,
  Review,
  ITSEventType,
  ReviewClickEventData,
  DiscourseData,
  ProblemStage,
} from 'its-pages';
import {
  DiscourseAnswers,
  SelectAnnotationOptionEventData,
  Tag,
} from '../common/page-data';

type AnnotState = 'correct' | 'wrong' | 'none' | 'has';

export interface DiscourseProps {
  pageData: DiscourseData;
  problemStage: ProblemStage;
  onChange?: (answers: DiscourseAnswers) => void;
  onReviewed?: () => void;
  onAnswerSelected?: (annotationId: string, selectedTagId: string) => void;
  isTeacherView: boolean;
  /**
   * If set, only the dialog fragments associated with one of the skill components should be highlighted
   * (Used for teacher dashboard)
   */
  tagIdsToEmphasize?: string[];
}

type AnnotatedDiscourseProps = DiscourseProps & {
  text: string;
  lineNum: number;
  showWrongAnswerTooltip: boolean;
};

const AnnotationStateColor: Record<AnnotState, ButtonProps['color']> = {
  none: 'warning',
  has: 'info',
  correct: 'success',
  wrong: 'error',
};

const getAnnotState = (
  currentAnswer: string,
  originalAnswer: string,
  correctAnswer: string,
  isCollectInput: boolean,
  isReview: boolean
): AnnotState => {
  if (isReview) {
    return correctAnswer !== currentAnswer ? 'wrong' : 'correct';
  } else {
    return currentAnswer ? 'has' : 'none';
  }
};

type MenuItemClickedHandler = (
  annotId: string,
  annotTagId: string,
  selectedTagId: string
) => void;

type DiscourseMenuItemProps = {
  value: Tag;
  isReview: boolean;
  isMenuReady: boolean;
  annotationTag: string;
  answer: string;
  answers: DiscourseAnswers;
  annot: DiscourseAnnotation;
  onReviewed?: (tag: Tag) => void;
  onMenuItemClicked: MenuItemClickedHandler;
  showRequestReviewPrompt: boolean;
};

const MenuItemStyle: SxProps<Theme> = {
  whiteSpace: 'normal',
  justifyContent: 'space-between',
  borderBottom: '1px solid',
  borderBottomColor: (theme: Theme) => theme.palette.divider,
  '&:last-of-type': {
    border: 'none'
  }
};

const DiscourseMenuItem = (props: DiscourseMenuItemProps) => {
  const {
    annotationTag,
    answer,
    isReview,
    isMenuReady,
    value,
    onReviewed,
    annot,
    onMenuItemClicked,
    showRequestReviewPrompt
  } = props;

  const opt = value.id;
  const isCorrect = annotationTag === opt;
  const isSelected = answer === opt;
  const showReview = isReview && (isCorrect || isSelected);
  const [openTip, setOpenTip] = useState(false);

  const handleMenuItemClick = useCallback(() => {
    if (isReview) {
      return;
    }
    onMenuItemClicked(annot.id, annot.tag, value.id);
  }, [onMenuItemClicked, isReview, annot.id, annot.tag, value.id]);

  return (
    <MenuItem
      key={opt}
      sx={MenuItemStyle}
      dense
      disabled={false}
      selected={isSelected}
      onClick={handleMenuItemClick}
      onFocus={() => setOpenTip(true)}
      onBlur={() => setOpenTip(false)}
    >
      <span>{value.reason.declarative}</span>
      {showReview && (
        <Review
          hasRequestReviewPrompt={showRequestReviewPrompt}
          isCorrectResponse={isCorrect}
          isSelected={isSelected}
          reason={value.explanation.markdown || value.explanation.text || ''}
          canRenderTooltip={isMenuReady}
          tipShouldOpen={openTip}
          onReviewed={() => {
            onReviewed?.(value);
          }}
        />
      )}
    </MenuItem>
  );
};

type ClickableDiscourseProps = {
  isReview: boolean;
  showRequestReviewPrompt: boolean;
  showTooltipOnDialog: boolean; // Have additional tooltip prompt for user to click on the dialog option
  annot: DiscourseAnnotation;
  answers: DiscourseAnswers;
  tags: Tag[];
  annotState: AnnotState;
  text: string;
  /** Index into `text` where the previous chunk ended */
  lastPos: number;
  onReviewed: (tag: Tag) => void;
  onMenuItemClicked: MenuItemClickedHandler;

  /**
   * If true, this clickable dialog should be styled in such a way to de-emphasize it.
   * Used only in context of teacher dashboard to show only parts of this question that relate to tags for
   * a particular card.
   */
  deemphasize?: boolean;
};

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height
  };
}

const ClickableDiscourseFragment = (props: ClickableDiscourseProps) => {
  const {
    annot,
    answers,
    deemphasize,
    isReview,
    showRequestReviewPrompt,
    showTooltipOnDialog,
    tags,
    annotState,
    onMenuItemClicked,
    onReviewed,
    lastPos,
    text,
  } = props;

  const regionColor = (deemphasize) ? 'primary' :
    AnnotationStateColor[annotState] ?? 'warning';
  const dialogButtonStyle = (deemphasize) ? 'text' : 'contained';
  const start = annot.anchor.offset;
  const end = annot.focus.offset;

  // button that anchors the choice menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // only one annotation can be open at a time
  const [openAnnot, setOpenAnnot] = useState<string>('');

  const isOpen = annot.id === openAnnot;

  /**
   * Used to deal with issue when the menu component with the answer options is anchored to a dialog near
   * the bottom of the viewable window, it will obscure the dialog. Need to use this flag to adjust
   * configuration settings for the menu component so it can appear above the dialog in that situation.
   */
  const [positionMenuBelowDialog, setMenuBelowDialog] = useState<boolean>(true);

  /** @todo Document. It's unclear to me what the purpose of it is. */
  const [openDialogTooltip, setOpenDialogTooltip] = useState(true);
  /**
   * Track the open/closed state of the menu.
   * Don't use anchorEl for this purpose as it prevents
   * (the menu's) `onClose` from being detected.
   */
  const [openMenu, setOpenMenu] = useState(false);
  /**
   * Detect when the open menu transition animation has finished,
   * so we can reliably measure the final layout for the tooltip
   * that pops up inside of it.
   */
  const [menuReady, setMenuReady] = useState(false);

  const handleMenuClose = useCallback(() => {
    setOpenAnnot('');
    setMenuReady(false);
    setOpenMenu(false);
  }, []);

  const handleMenuItemClick = useCallback<MenuItemClickedHandler>(
    (id, tag, selectedTag) => {
      onMenuItemClicked(id, tag, selectedTag);
      handleMenuClose();
    },
    [onMenuItemClicked, handleMenuClose]
  );

  /** Track the end of the menu open transition (animation) */
  const menuRef = useRef(null);
  const handleMenuTransition = useCallback<
    TransitionEventHandler<HTMLDivElement>
  >((e) => {
    if (
      e.target !== menuRef.current ||
      e.propertyName !== 'transform' ||
      e.type !== 'transitionend'
    ) {
      return;
    }
    setMenuReady(openMenu);
  }, [openMenu]);

  const handleRegionClicked = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      // Based on the position of the dialog that was clicked, determine whether the menu containing the
      // answer options should appear below/above that dialog so it is not hidden or cutoff by that menu.
      let answersBelowDialog = true;
      const dialogBounds = event?.currentTarget.getBoundingClientRect();
      if (dialogBounds) {
        const { height: windowHeight } = getWindowDimensions();
        const diff = Math.abs(windowHeight - dialogBounds.bottom);
        // TODO:
        // Using hardcoded 'height' of the menu
        if (diff < 170) {
          answersBelowDialog = false;
        }
      }
      setMenuBelowDialog(answersBelowDialog);
      setAnchorEl(event?.currentTarget);
      setOpenMenu(true);
      setOpenAnnot(annot.id);
      if (showTooltipOnDialog)
        setOpenDialogTooltip(false);
    },
    [annot, showTooltipOnDialog]
  );

  const plainTextPrefix = useMemo(
    () =>
      lastPos < start ? (
        <span key={lastPos}>{text.substring(lastPos, start)}</span>
      ) : (
        <></>
      ),
    [lastPos, start, text]
  );

  const menuAnchorOrigin = useMemo(() => {
    return {
      vertical: positionMenuBelowDialog ? 'bottom': 'top',
      horizontal: 'left'
    };
  }, [positionMenuBelowDialog]);

  const menuTransformOrigin = useMemo(() => {
    return {
      vertical: positionMenuBelowDialog ? 'top' : 'bottom',
      horizontal: 'left'
    };
  }, [positionMenuBelowDialog]);

  // Has feedback to review is true IF
  // - correct option NOT selected
  // - incorrect option IS selected
  const indexForOption = isReview
    ? tags.findIndex((value) => {
        const optionName = value.id;
        const answer = answers[annot.id];
        const isCorrect = annot.tag === optionName;
        const isSelected = answer === optionName;
        return (isCorrect && !isSelected) || (!isCorrect && isSelected);
      })
    : -1;

  return (<>
    <Tooltip
      open={showTooltipOnDialog && openDialogTooltip}
      arrow
      placement='top-start'
      title={'Click/Tap the incorrect dialog to review.'}>
      <div>
      {plainTextPrefix}
      <Button
        key={start}
        id={annot.id + '-region'}
        size='small'
        component='span'
        variant={dialogButtonStyle}
        color={regionColor}
        sx={{ display: 'inline', textTransform: 'none' }}
        aria-haspopup='true'
        aria-controls={isOpen ? annot.id + '-menu' : undefined}
        aria-expanded={isOpen ? 'true' : undefined}
        onClick={handleRegionClicked}
      >
        {text.substring(start, end)}
      </Button>
      </div>
    </Tooltip>
    <Menu
      id={annot.id + '-menu'}
      anchorEl={anchorEl}
      open={openMenu}
      onClose={handleMenuClose}
      onTransitionEnd={handleMenuTransition}
      PaperProps={{ ref: menuRef }}
      anchorOrigin={menuAnchorOrigin}
      transformOrigin={menuTransformOrigin}
      MenuListProps={{
        'aria-labelledby': annot.id + '-region',
      }}
    >
      {Object.values(tags).map((tag, index) => {
        return <DiscourseMenuItem
          key={tag.id}
          isReview={isReview}
          isMenuReady={menuReady}
          answers={answers}
          showRequestReviewPrompt={indexForOption === index && showRequestReviewPrompt}
          onMenuItemClicked={handleMenuItemClick}
          onReviewed={onReviewed}
          value={tag}
          annot={annot}
          answer={answers[annot.id]}
          annotationTag={annot.tag}
        />
      })}
    </Menu>
  </>);
};


// TODO: Return both linenumber of sub-index.
// Sub-index needed
function getIncorrectOption(pageData: DiscourseData) {
  let lineNumberWithIncorrectAnswer = -1;
  const { annotations, answers, content } = pageData;
  content.forEach((_, lineNumber) => {
    const selections = annotations.filter((annot) => {
      return annot.anchor.path[0] === lineNumber;
    }).sort((a, b) => {
      return a.anchor.offset - b.anchor.offset
    });

    selections.forEach((annotation, index) => {
      const { id, tag } = annotation;
      const userAnswer = answers[id];
      const correctAnswer = tag;
      if (correctAnswer) {
        const isWrong = userAnswer != correctAnswer;
        if (isWrong && lineNumberWithIncorrectAnswer < 0) {
          lineNumberWithIncorrectAnswer = lineNumber;
        }
      }
    });
  });
  return lineNumberWithIncorrectAnswer;
}

// take text and annotations and return text separated in spans (each could be either non-annotation or annotation)
// for each annotation span style it differently and add an interactive tippy for tag selections
function AnnotatedDiscourse(props: AnnotatedDiscourseProps): JSX.Element {
  const {
    isTeacherView,
    tagIdsToEmphasize,
    text,
    lineNum,
    pageData,
    problemStage,
    showWrongAnswerTooltip,
    onAnswerSelected,
    onChange,
    onReviewed,
  } = props;

  const { annotations, annotationOptions } = pageData;
  const [answers, setAnnotAnswers] = useState<DiscourseAnswers>(
    pageData.answers || {}
  );

  const isSecondTry = problemStage === ProblemStage.SecondTry;
  const isCollectInput = problemStage === ProblemStage.CollectInput;
  const isReview = problemStage === ProblemStage.ShowResult;

  const originalAnswers = useRef(answers);

  const handleMenuItemClick = useCallback<MenuItemClickedHandler>(
    (annotId, annotTagId, selectedTagId) => {
      if (isCollectInput || isSecondTry) {
        const newAnswers: DiscourseAnswers = { ...pageData.answers };
        newAnswers[annotId] = selectedTagId;
        setAnnotAnswers(newAnswers);
        onChange?.(newAnswers);
      }

      onAnswerSelected?.(annotId, selectedTagId);
      const eventData: SelectAnnotationOptionEventData = {
        annotId,
        selected: true,
        annotTagId,
        selectedTagId,
      };
      pageData.recordEvent(ITSEventType.selectOption, eventData);
    },
    [pageData, isCollectInput, isSecondTry, answers, onAnswerSelected, onChange]
  );

  /** Used exclusively for detecting changes to `problemStage` */
  const stageRef = useRef(problemStage);

  /**
   * Make sure originalAnswers is updated w/ problemStage changes
   * from something else to SecondTry, ignoring changes to `answers`.
   * @todo There has got to be a better way of doing this!
   */
  useEffect(() => {
    if (problemStage !== stageRef.current) {
      if (problemStage === ProblemStage.SecondTry) {
        originalAnswers.current = { ...answers };
      }
      stageRef.current = problemStage;
    }
  }, [problemStage, answers]);

  const [hasReviewedOnce, setHasReviewedOnce] = useState<boolean>(false); 
  const handleReviewed = useCallback(
    (tag: Tag) => {
      setHasReviewedOnce(true);
      const data: ReviewClickEventData = {
        id: tag.id,
      };
      pageData.recordEvent(ITSEventType.reviewClick, data);
      onReviewed?.();
    },
    [pageData, onReviewed]
  );

  const annots = useMemo(
    () => annotations.filter((a) => a.anchor.path[0] === lineNum),
    [annotations, lineNum]
  );

  const sortedAnns = useMemo(
    () => annots.sort((a, b) => a.anchor.offset - b.anchor.offset),
    [annots]
  );

  let last = 0;
  let hasFoundWrongAnswerAlready = false;
  const onlyEmphasizePortions = tagIdsToEmphasize && tagIdsToEmphasize.length > 0;
  const regions = sortedAnns.map((annot) => {
    const annotOptions = annotationOptions[annot.id];

    const currentAnswer = answers[annot.id];
    const originalAnswer = originalAnswers.current[annot.id];

    const annotState = getAnnotState(
      currentAnswer,
      originalAnswer,
      annot.tag,
      isCollectInput,
      isReview
    );

    let deemphasizeThisFragment = false;
    if (onlyEmphasizePortions) {
      // Emphasize the fragment only if
      // - the statement is associated with one of the tags
      // - the user response was associated with one of the tags
      deemphasizeThisFragment = tagIdsToEmphasize.indexOf(annot.tag) < 0 &&
        tagIdsToEmphasize.indexOf(currentAnswer) < 0;
    }

    // Create copy of the tags that act as the answer options, per fragment we want to
    // potentially overwrite the review feedback if custom feedback exists.
    const annotationToCustomFeedback = pageData.annotationCustomFeedback?.find((value) => {
      return value.id === annot.id;
    });
    const tagIdToFeedback = annotationToCustomFeedback ? annotationToCustomFeedback.tagIdToFeedback : undefined;
    const tagOptionsCopy = annotOptions.map((value) => {
      const copy = {...value };
      if (tagIdToFeedback && tagIdToFeedback[value.id]) {
        copy.explanation.markdown = tagIdToFeedback[value.id];
      }
      return copy;
    });

    const isWrong = annotState === 'wrong';
    const showTooltipOnDialog = isWrong && !hasFoundWrongAnswerAlready && showWrongAnswerTooltip && isReview;
    const comp = (
      <ClickableDiscourseFragment
        deemphasize={deemphasizeThisFragment}
        showRequestReviewPrompt={!isTeacherView && !hasReviewedOnce}
        showTooltipOnDialog={showTooltipOnDialog && !isTeacherView}
        key={`${annot.id}-cfrag`}
        annot={annot}
        annotState={annotState}
        answers={answers}
        isReview={isReview}
        lastPos={last}
        tags={tagOptionsCopy}
        text={text}
        onReviewed={handleReviewed}
        onMenuItemClicked={handleMenuItemClick}
      />
    );
    if (showTooltipOnDialog) {
      hasFoundWrongAnswerAlready = true;
    }

    last = annot.focus.offset;
    return comp;
  });

  const endsWithPlaintext = last < text.length;

  return (
    <div>
      {regions}
      {endsWithPlaintext && (
        <span key={last}>{text.substring(last, text.length)}</span>
      )}
    </div>
  );
}

export const Discourse = (props: DiscourseProps) => {
  const { pageData } = props;
  const patient = pageData.scenario?.metadata.patient.name || '';
  const content = pageData.content;

  // For the line number, determine which one is incorrect and should display a tooltip
  const wrongLineNumber = getIncorrectOption(pageData);
  return (
    <Paper sx={{ m: '1rem 0' }} elevation={5}>
    <Grid container>
      {content.map((line, lineNumber: number) => {
        return (
          <Grid
            container
            direction='row'
            spacing={0.2}
            sx={[
              { m: 1, margin: 0, padding: '0.5em', comment: 'discourse row' },
              line.type === 'client' && { bgcolor: '#9992' },
            ]}
            item
            key={line.children[0].text}
          >
            <Grid item xs={1.2} sx={{ textAlign: 'left', minWidth: '5em' }}>
              <b>{line.type === 'client' ? patient : 'Provider'}: </b>
            </Grid>
            <Grid xs={10.8} item>
              <AnnotatedDiscourse
                showWrongAnswerTooltip={wrongLineNumber === lineNumber}
                text={line.children[0].text}
                lineNum={lineNumber}
                {...props}
              />
            </Grid>
          </Grid>
        );
      })}
    </Grid>
    </Paper>
  );
};
